/**
 * PortfolioUpload - Drag-and-drop file upload for Fidelity PDF/CSV exports.
 */

import { useState, useCallback, useRef } from 'react';
import { Upload, FileText, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { InfoTooltip } from '@/components/ui/Tooltip';
import type { UploadResult } from '@/types/portfolio';

interface PortfolioUploadProps {
  onUpload: (file: File) => Promise<UploadResult | null>;
  isUploading: boolean;
  className?: string;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
}

export function PortfolioUpload({ onUpload, isUploading, className }: PortfolioUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [result, setResult] = useState<UploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(async (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext !== 'pdf' && ext !== 'csv') {
      setError('Please upload a PDF or CSV file');
      return;
    }

    setError(null);
    setResult(null);
    const uploadResult = await onUpload(file);
    if (uploadResult) {
      setResult(uploadResult);
    }
  }, [onUpload]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  return (
    <div className={className}>
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => inputRef.current?.click()}
        className={cn(
          'border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all duration-200',
          isDragging
            ? 'border-primary bg-primary/5'
            : 'border-border hover:border-text-muted hover:bg-surface-light/50',
          isUploading && 'pointer-events-none opacity-60'
        )}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.csv"
          onChange={handleInputChange}
          className="hidden"
        />

        {isUploading ? (
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-10 h-10 text-primary animate-spin" />
            <p className="text-text-primary font-medium">Processing portfolio...</p>
            <p className="text-sm text-text-muted">Parsing positions and importing data</p>
          </div>
        ) : result ? (
          <div className="flex flex-col items-center gap-3">
            <CheckCircle className="w-10 h-10 text-green-400" />
            <p className="text-text-primary font-medium">{result.message}</p>
            {(result.cash_balance ?? 0) > 0 && (
              <p className="text-xs text-text-muted">
                Includes {formatCurrency(result.cash_balance!)} in cash/money market
              </p>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setResult(null);
              }}
              className="text-sm text-primary hover:underline"
            >
              Upload another file
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <Upload className="w-10 h-10 text-text-muted" />
            <div>
              <p className="text-text-primary font-medium">
                Drop your portfolio export here
              </p>
              <p className="text-sm text-text-muted mt-1">
                or click to browse
              </p>
            </div>
            <div className="flex items-center gap-2 text-xs text-text-muted mt-2">
              <FileText className="w-3.5 h-3.5" />
              <span>Supported: Fidelity PDF and CSV exports</span>
              <InfoTooltip
                content={
                  <div className="space-y-2">
                    <p className="font-medium text-text-primary">How to export from Fidelity:</p>
                    <ol className="list-decimal list-inside space-y-1 text-text-secondary">
                      <li>Log in to Fidelity.com</li>
                      <li>Go to the Positions page</li>
                      <li>Click the Download icon (top right)</li>
                      <li>Select CSV format</li>
                    </ol>
                  </div>
                }
                position="bottom"
              />
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-3 flex items-center gap-2 text-sm text-red-400">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
